/**
 * Created by Richard on 29.06.22
 */

/**
 * Export a PR to arrays of data
 * @param {!Object} pr
 * @param {!number} prId
 * @return {!Array<*>}
 */
export const exportPR = (pr, prId) => {
    const exporter = new Exporter();
    const mappings = Array.from(exporter.mappings.values());
    const exporters = Array.from(exporter.exporters.values());

    // This map will contain columns names as keys, and the data as values. Values can be arrays.
    const map = new Map();

    let maxDepth = 1;

    // Create an array containing all the values, in a format where singles values (i.e. data that cannot have multiple
    // values) are simple string, whereas multiple values (e.g. materials, accounts, etc.) are arrays.
    const values = exporters.flatMap((e) => {
        const val = e(pr);
        if (Array.isArray(val)) {
            if (val.filter((v) => Array.isArray(v)).length > 0) {
                return val.map((v) => {
                    if (Array.isArray(v)) {
                        maxDepth = Math.max(maxDepth, v.length);
                        return invertArray(v);
                    } else {
                        return v;
                    }
                });
            } else {
                return val;
            }
        } else {
            return val;
        }
    });

    // Set the values for each title
    mappings
        .flat()
        .forEach((title, idx) => map.set(title, values[idx]));

    // Flat the multiple values (arrays)
    for (const [key, value] of map) {
        if (Array.isArray(key)) {
            key.forEach((title, idx) => {
                map.set(title, value[idx]);
            });
        }
    }

    const result = createTwoDimArray(mappings.flat(2).length, maxDepth);

    // Create each line of the Excel file
    mappings.flat(2).forEach((title, idx) => {
        const val = map.get(title);

        if (!Array.isArray(val)) {
            result[0][idx] = val;
        } else if (val.length === 1) {
            result[0][idx] = val[0];
        } else {
            val.forEach((v, i) => {
                // if (!result[i]) {
                //     result[i] = [];
                // }
                result[i][idx] = v;
            });
        }
    });

    // cleanUp the array because somewhere I am adding an emty row
    const cleanedResults = [];
    result.forEach((item) => {
        if ( !item.every((element) => element === '')) {
            cleanedResults.push(item);
        }
    });
    // Add the PR id at the beginning of the line
    for (let i = 0; i < cleanedResults.length; i++) {
        cleanedResults[i].unshift(prId);
    }

    // now we must duplicate the content from the first line to all the lines ( if we have empty cells)
    let tmpFirstLine =[];
    cleanedResults.forEach((item, index) => {
        if ( index === 0) {
            tmpFirstLine = [...item];
        } else {
            item.forEach((childItem, childIndex) => {
                if ( childItem === '') {
                    item[childIndex] = tmpFirstLine[childIndex];
                }
            });
        }
    });
    return cleanedResults;
};

/**
 * Return an array containing all the column names
 * @return {string[]}
 */
export const exportColumnsNames = () => {
    const exporter = new Exporter();
    const mappings = Array.from(exporter.mappings.values());

    const result = new Array(mappings.flat(2).length);

    mappings.flat(2).forEach((title, idx) => {
        result[idx] = title;
    });

    result.unshift('EPR Id');

    return result;
};

const createTwoDimArray = (column, line) => {
    return new Array(Math.max(line)).fill(0).map(() => new Array(column).fill(''));
};

/**
 * @param {[]} array
 * @return {any[]}
 */
const invertArray = (array) => {
    if (!Array.isArray(array)) {
        return [''];
    }
    if (!Array.isArray(array[0])) {
        return array;
    }
    const result = createTwoDimArray(array.length, array[0].length, 1);

    for (let i = 0; i < array.length; i++ ) {
        for (let j = 0; j < array[0].length; j++) {
            result[j][i] = array[i][j];
        }
    }

    return result;
};

/**
 * Utility class to export PRs
 */
class Exporter {
    /**
     * Create an exporter able to export any PR
     */
    constructor() {
        /**
         * @private
         * @type {Map<!Exporter.PRField, !Array<string>>}
         */
        this._mappings = new Map();
        /**
         * @private
         * @type {Map<!Exporter.PRField, function>}
         */
        this._exporters = new Map();

        // Exportation function for each field to allow easy iteration
        this._exporters.set(Exporter.PRField.prNumber, Exporter.prNumber);
        this._exporters.set(Exporter.PRField.materialId, Exporter.materialIds);
        this._exporters.set(Exporter.PRField.source, Exporter.source);
        this._exporters.set(Exporter.PRField.sourcing, Exporter.sourcing);
        this._exporters.set(Exporter.PRField.contract, Exporter.contract);
        this._exporters.set(Exporter.PRField.vendorCode, Exporter.vendorCode);
        this._exporters.set(Exporter.PRField.vendorName, Exporter.vendorName);
        this._exporters.set(Exporter.PRField.oneTimeVendor, Exporter.oneTimeVendor);
        this._exporters.set(Exporter.PRField.creationDate, Exporter.creationDate);
        this._exporters.set(Exporter.PRField.submissionDate, Exporter.submissionDate);
        this._exporters.set(Exporter.PRField.projectTitle, Exporter.projectTitle);
        this._exporters.set(Exporter.PRField.wayOfBuying, Exporter.wayOfBuying);
        this._exporters.set(Exporter.PRField.spender, Exporter.spender);
        this._exporters.set(Exporter.PRField.requester, Exporter.requester);
        this._exporters.set(Exporter.PRField.sbApprover, Exporter.sbApprover);
        this._exporters.set(Exporter.PRField.headerDetails, Exporter.headerDetails);
        this._exporters.set(Exporter.PRField.internalComment, Exporter.internalComment);
        this._exporters.set(Exporter.PRField.externalComment, Exporter.externalComment);
        this._exporters.set(Exporter.PRField.sendingOptions, Exporter.sendingOptions);
        this._exporters.set(Exporter.PRField.financialInfo, Exporter.financialInfo);
        this._exporters.set(Exporter.PRField.materials, Exporter.materials);
        this._exporters.set(Exporter.PRField.attachmentsInfo, Exporter.attachmentsInfo);
        this._mappings.set(Exporter.PRField.prNumber, ['SAP PR number']);
        this._mappings.set(Exporter.PRField.materialId, ['Line Item Number']);
        this._mappings.set(Exporter.PRField.source, ['Initiated From']);
        this._mappings.set(Exporter.PRField.sourcing, ['Sourcing event ID']);
        this._mappings.set(Exporter.PRField.contract, ['Contract ID']);
        // Exportation mappings to easily get corresponding column names
        // if a line has no comment, it means it is a primitive type exported as is
        this._mappings.set(Exporter.PRField.vendorCode, ['Vendor code']);
        this._mappings.set(Exporter.PRField.vendorName, ['Vendor name']);
        this._mappings.set(Exporter.PRField.oneTimeVendor,
            [
                'One-time vendor name',
            ],
        );
        // exported to ISO-8601 compliant string
        this._mappings.set(Exporter.PRField.creationDate, ['Creation Date and Time']);
        this._mappings.set(Exporter.PRField.submissionDate, ['Submission Date']);

        this._mappings.set(Exporter.PRField.projectTitle, ['Project title']);
        // label-value: options in src/easy_request/components/_helpers.js, we only keep label.
        this._mappings.set(Exporter.PRField.wayOfBuying, ['Way of buying']);
        // label-value: can be two different options based on code logic, need to store both
        this._mappings.set(Exporter.PRField.spender, ['Spender']);
        // label-value: can be two different options based on code logic, need to store both
        this._mappings.set(Exporter.PRField.requester, ['Requester']);
        this._mappings.set(Exporter.PRField.sbApprover, ['SB Approver']);
        // composite attribute with many fields
        this._mappings.set(Exporter.PRField.headerDetails,
            [
                'Company code',
                'Purchase organization',
                'Purchasing group',
                'Plant',
                'Storage Location',
                'Name',
                'Name2',
                'Street',
                'House Number',
                'State/Province',
                'City',
                'Postal Code',
                'Country',
                'Requested delivery date',
                'Validity start (FO)',
                'Validity end (FO)',
                'Payment terms',
                'New payment terms',
                'Ordering address',
                'Invoice partner',
                'Incoterms',
                'Supplier status',
                'Good Receipt Required',
                'GR-Based Invoice Verification',
                'ERS',

            ]);
        this._mappings.set(Exporter.PRField.internalComment, ['Internal comment (headers)']);
        this._mappings.set(Exporter.PRField.externalComment, ['External comment (headers)']);
        // composite value
        this._mappings.set(Exporter.PRField.sendingOptions, [
            'Send copy to requester',
            'Send copy to spender',
            'Send copy to other',
            'Other\'s address',
            'Send copy to supplier',
            'Supplier address',
            'Hide Price',
            // 'Hide price',
        ]);
        this._mappings.set(Exporter.PRField.financialInfo, [
            'Different payment accounts for each item',
            [
                'Item category',
                'Account type',
                'WBS',
                'Cost center',
                'Profit center',
                'GL number',
                'Allocated for',
            ],
        ]);
        // Array of composite values
        this._mappings.set(Exporter.PRField.materials, [
            [
                'Material number',
                'Material description',
                'Material quantity',
                'Material unit',
                'Material price',
                'Material currency',
                'Subtotal',
                'Free Item',
                'Material per',
                'Material tax code',
                'Material l3 Category',
                'Vendor material no',
                'Material type',
                'PSL Material ID',
                'PSL ID',
                'Requested delivery date (line item)',
                'Plant (line item)',
                'Storage Location (line item)',
                'Name (line item)',
                'Name2 (line item)',
                'Street (line item)',
                'House Number (line item)',
                'State/Province (line item)',
                'City (line item)',
                'Postal Code (line item)',
                'Country (line item)',
                'Internal comment (line item)',
                'External comment (line item)',
            ],
        ]);
        this._mappings.set(Exporter.PRField.attachmentsInfo, [
            [
                'Attachment topic (optional)',
            ],
            [
                'Attachment topic (Competitive bidding justification)',
            ],
            'Exception Reason',
            'Compliance Indicator',
        ]);
    }
    /**
     * Get the vendorName exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The vendorName exported value.
     */
    static vendorName(pr) {
        return [Exporter.stringDefaultOrValue(pr.vendorName)];
    }
    /**
     * Get the vendorCode exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The vendorCode exported value.
     */
    static vendorCode(pr) {
        return [Exporter.stringDefaultOrValue(pr.vendorCode)];
    }
    /**
     * Get the oneTimeVendor exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The vendorCode exported value.
     */
    static oneTimeVendor(pr) {
        return pr.oneTimeVendor ? [
            Exporter.stringDefaultOrValue(pr.oneTimeVendor.supplierName),
        ] : [''];
    }

    /**
     * Get the creationDate exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The creationDate exported value.
     */
    static creationDate(pr) {
        return [Exporter.dateDefaultOrValue(pr.creationDate)];
    }

    /**
     * Get the submissionDate exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The submissionDate exported value.
     */
    static submissionDate(pr) {
        return [Exporter.dateDefaultOrValue(pr.submissionDate)];
    }

    /**
     * Get the prNumber exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The prNumber exported value.
     */
    static prNumber(pr) {
        return [Exporter.stringDefaultOrValue(pr.prNumber)];
    }
    /**
     * Get the materialIds exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The materialIds exported value.
     */
    static materialIds(pr) {
        /**
         * @param {!Material} material
         * @return {!Array<!*>}
         */
        const exportMaterialId = (material) => {
            const materialId = (material.id + 1) * 10;
            const nrOfAccounts = pr.financialInfo?.accountDetailsByMaterial
                ?.find((account) => account.material.id === material.id)
                ?.accounts.length || 1;

            return Array(nrOfAccounts).fill(materialId);
        };

        const rsp = pr.materials
            .map((material) => exportMaterialId(material))
            .flat();

        return [Exporter.arrayWithDefaultIfEmpty(rsp, 1, 1)];
    }
    /**
     * Get the projectTitle exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The projectTitle exported value.
     */
    static projectTitle(pr) {
        return [Exporter.stringDefaultOrValue(pr.projectTitle)];
    }
    /**
     * Get the wayOfBuying exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The wayOfBuying exported value.
     */
    static wayOfBuying(pr) {
        return [Exporter.stringDefaultOrValue(pr.wayOfBuying.currentValue.label)];
    }
    /**
     * Get the spender exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The spender exported value.
     */
    static spender(pr) {
        // return [Exporter.stringDefaultOrValue(pr.spender.label), Exporter.stringDefaultOrValue(pr.spender.value)];
        return [Exporter.stringDefaultOrValue(pr.spender.label)];
    }

    /**
     * Get the requester exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The requester exported value.
     */
    static requester(pr) {
        // return [Exporter.stringDefaultOrValue(pr.requester.label), Exporter.stringDefaultOrValue(pr.spender.value)];
        return [Exporter.stringDefaultOrValue(pr.requester.label)];
    }

    /**
     * Get the sb approver exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<string>} The sb approver exported value.
     */
    static sbApprover(pr) {
        return [Exporter.stringDefaultOrValue(pr.sbValidation.sbApprover)];
    }

    /**
     * Get the header details exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<!*>} The header details exported value.
     */
    static headerDetails(pr) {
        const header = pr.headerDetails;
        return [
            Exporter.stringDefaultOrValue(header.companyCode.label),
            Exporter.stringDefaultOrValue(header.purchaseOrganization.label),
            Exporter.stringDefaultOrValue(header.purchasingGroup.label),
            Exporter.stringDefaultOrValue(header.defaultPlant.label),
            Exporter.stringDefaultOrValue(header.defaultStorageLocation.label),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.name),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.name2),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.street),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.houseNumber),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.state),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.city),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.postalCode),
            Exporter.stringDefaultOrValue(header.defaultDeliveryAddress.country),
            Exporter.dateDefaultOrValue(header.defaultRequestedDeliveryDate),
            Exporter.dateDefaultOrValue(header.validityStart),
            Exporter.dateDefaultOrValue(header.validityEnd),
            Exporter.stringDefaultOrValue(header.paymentTerms.label),
            Exporter.stringDefaultOrValue(header.newPaymentTerms.label),
            Exporter.stringDefaultOrValue(header.orderingAddress.label),
            Exporter.stringDefaultOrValue(header.invoicePartner.label),
            Exporter.stringDefaultOrValue(header.incoterms.label),
            Exporter.stringDefaultOrValue(header.supplierStatus),
            Exporter.booleanDefaultOrValue(header.goodsReceipt),
            Exporter.booleanDefaultOrValue(header.grBasedInvoiceVerification),
            Exporter.booleanDefaultOrValue(header.ers),
        ];
    }

    /**
     * Get the internalComment exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {Array<!string>} The internalComment exported value.
     */
    static internalComment(pr) {
        return [Exporter.stringDefaultOrValue(pr.internalComment)];
    }
    /**
     * Get the externalComment exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {Array<!string>} The externalComment exported value.
     */
    static externalComment(pr) {
        return [Exporter.stringDefaultOrValue(pr.externalComment)];
    }
    /**
     * Get the sendingOptions exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<!*>} The sendingOptions exported value.
     */
    static sendingOptions(pr) {
        const sendingOptions = pr.sendingOptions;
        return [
            Exporter.booleanDefaultOrValue(sendingOptions.requester),
            Exporter.booleanDefaultOrValue(sendingOptions.spender),
            Exporter.booleanDefaultOrValue(sendingOptions.other),
            Exporter.stringDefaultOrValue(sendingOptions.otherAddress),
            Exporter.booleanDefaultOrValue(sendingOptions.supplier),
            Exporter.arrayWithDefaultIfEmpty(sendingOptions.supplierAddresses, 0, 0),
            Exporter.hidePriceValueCheck(sendingOptions.hidePrice),
        ];
    }
    /**
     * Get the financialInfo exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<!*>} The financialInfo exported value.
     */
    static financialInfo(pr) {
        const info = pr.financialInfo;
        /**
         * @param {!Account} account
         * @return {!Array<string>}
         */
        const exportAccount = (account) => {
            // Cost center -> Costcenter / Gl number
            // WBS / Project -> WBS / GL number
            // Profit center -> Profit Center / GL number
            // Statistical -> WBS / Costcenter / GL number
            const showWBS = account.accountType.label === 'WBS / Project' || account.accountType.label === 'Statistical WBS';
            const showCostCenter = account.accountType.label === 'Cost Center' || account.accountType.label === 'Statistical WBS';
            const showProfitCenter = account.accountType.label === 'Profit Center';
            return [
                Exporter.stringDefaultOrValue(account.itemCategory.label),
                Exporter.stringDefaultOrValue(account.accountType.label),
                Exporter.stringDefaultOrValue(showWBS ? account.wbs?.label : ''),
                Exporter.stringDefaultOrValue(showCostCenter ? account.costCenter?.label : ''),
                Exporter.stringDefaultOrValue(showProfitCenter ? account.profitCenter?.label : ''),
                Exporter.stringDefaultOrValue(account.glNumber.label),
                Exporter.stringDefaultOrValue(account.allocateFor),
            ];
        };
        const accountDetailsByMaterial = Exporter.arrayWithDefaultIfEmpty(info.accountDetailsByMaterial
            .map((accountMaterial) => {
                return accountMaterial.accounts.map((account) => [...exportAccount(account)]);
            }).flat(), 1, 7);
        const accounts = Exporter.arrayWithDefaultIfEmpty(info.accounts.map((account) => {
            return [...exportAccount(account)];
        }), 1, 7);

        const joinedData = accounts.concat(accountDetailsByMaterial).filter((item) => item.some((elem) => elem!== ''));
        return [
            Exporter.booleanDefaultOrValue(info.areAccountSplitByMaterial),
            joinedData,
        ];
    }
    /**
     * Get the materials exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<!Array<!*>>} The materials exported value.
     */
    static materials(pr) {
        /**
         * @param {!Material} material
         * @return {!Array<!*>}
         */
        const exportMaterial = (material) => {
            const address = material.deliveryAddress;
            const subTotal = material.quantity * material.price || 0;
            return [
                Exporter.stringDefaultOrValue(material.name),
                Exporter.stringDefaultOrValue(material.description),
                Exporter.stringDefaultOrValue(material.quantity),
                Exporter.stringDefaultOrValue(material.unit),
                Exporter.stringDefaultOrValue(material.price),
                Exporter.stringDefaultOrValue(material.currency.label),
                subTotal,
                Exporter.booleanDefaultOrValue(material.freeItem),
                Exporter.stringDefaultOrValue(material.per),
                Exporter.stringDefaultOrValue(material.taxCode.label),
                Exporter.stringDefaultOrValue(material.l3Category),
                Exporter.stringDefaultOrValue(material.supplierPartId),
                Exporter.stringDefaultOrValue(material.type),
                Exporter.stringDefaultOrValue(material.uniqueSourcingToolId),
                Exporter.stringDefaultOrValue(material.pslId),
                Exporter.dateDefaultOrValue(material.requestedDeliveryDate),
                Exporter.stringDefaultOrValue(material.plant.label),
                Exporter.stringDefaultOrValue(material.storageLocation.label),
                Exporter.stringDefaultOrValue(address.name),
                Exporter.stringDefaultOrValue(address.name2),
                Exporter.stringDefaultOrValue(address.street),
                Exporter.stringDefaultOrValue(address.houseNumber),
                Exporter.stringDefaultOrValue(address.state),
                Exporter.stringDefaultOrValue(address.city),
                Exporter.stringDefaultOrValue(address.postalCode),
                Exporter.stringDefaultOrValue(address.country),
                Exporter.stringDefaultOrValue(material.internalComments),
                Exporter.stringDefaultOrValue(material.supplierComments),

            ];
        };
        return Exporter.arrayWithDefaultIfEmpty([pr.materials.map((material) => [...exportMaterial(material)])], 1, 28);
    }

    /**
     * Get the sourcing event id exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {string} The sourcing event idexported value.
     */
    static sourcing(pr) {
        return pr.sourcingEventID || '';
    }

    /**
     * Get the contract id exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {boolean} The contract id exported value.
     */
    static contract(pr) {
        return pr.contractID || '';
    }
    /**
     * Get the attachments_info exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {!Array<!Array<!*>>} The attachments_info exported value.
     */
    static attachmentsInfo(pr) {
        const info = pr.attachmentsInfo;

        /**
         * @param {!attachments} attachments
         * @return {!Array<string>}
         */
        const exportAllAttachemnts = (attachments) => {
            const res = [];
            const tmpAttachmentsTopics = attachments.map((att) => att.topic);
            if ( tmpAttachmentsTopics.length > 0 ) {
                res.push('Yes (' + tmpAttachmentsTopics.join(',') + ')');
            } else {
                res.push('No');
            }
            return res;
        };
        return [
            Exporter.arrayWithDefaultIfEmpty(exportAllAttachemnts(info.mainAttachments), 1, 5),
            Exporter.arrayWithDefaultIfEmpty(exportAllAttachemnts(info.competitiveBiddingJustificationAttachments), 1, 5),
            Exporter.stringDefaultOrValue(info.selectedException),
            Exporter.stringDefaultOrComplienceValue(info.complianceIndicator),
        ];
    }

    /**
     * Get the composing exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {boolean} The composing exported value.
     */
    static composing(pr) {
        return pr.composing;
    }
    /**
     * Get the source exported value.
     * @private
     * @param {!PurchaseRequest} pr
     * @return {string} The source exported value.
     */
    static source(pr) {
        const SOURCES_TO_STRING_VALUE = {
            'psl': 'PSL',
            'popup': 'Non-PSL (Describe Your Need)',
            'sourcingevent': 'Sourcing Event (Agency Mania)',
            'agency mania': 'Sourcing Event (Agency Mania)',
            'tsf': 'Sourcing Event (TSF)',
        };
        return SOURCES_TO_STRING_VALUE[pr.source.toLowerCase()] || pr.source;
    }
    /**
     * Get the psl vendor contact email
     * @private
     * @param {!PurchaseRequest} pr
     * @return {string[]}
     */
    static vendorContactEmails(pr) {
        return pr.vendorContactEmails;
    }
    /**
     * Get whether the supplier of this PR can be edited
     * @private
     * @param {!PurchaseRequest} pr
     * @return {boolean}
     */
    static editableSupplier(pr) {
        return pr.editableSupplier;
    }

    /**
     * @private
     * @param {string} value
     * @return {string} value if defined or empty string default
     */
    static stringDefaultOrValue(value) {
        return value ? value : '';
    }
    /**
     * @private
     * @param {string} value
     * @return {string} value if defined or empty string default
     */
    static stringDefaultOrComplienceValue(value) {
        return value ? value : 'P - Compliant';
    }

    /**
     * @private
     * @param {number} value
     * @return {number} value if defined or 0 default
     */
    static numberDefaultOrValue(value) {
        return value ? value : 0;
    }

    /**
     * @private
     * @param {boolean} value
     * @return {boolean}
     */
    static booleanDefaultOrValue(value) {
        return value ? 'Yes' : 'No';
    }
    /**
     * @private
     * @param {boolean} value
     * @return {string}
     */
    static hidePriceValueCheck(value) {
        return value ? 'Yes' : 'No';
    }
    /**
     * @private
     * @param {?Date | string} value
     * @return {string}
     */
    static getReadableDate(value) {
        const readableDateOptions = {month: 'short', day: 'numeric', year: 'numeric'};
        const readableDate = value.toLocaleString('en-US', readableDateOptions);
        return readableDate;
    }

    /**
     * @private
     * @param {?Date | string} value
     * @return {string}
     */
    static dateDefaultOrValue(value) {
        if (typeof value === 'string' && value !== '' && value !== 'Invalid Date') {
            const newDate = new Date(value);
            return Exporter.getReadableDate(newDate);
        }
        return typeof value?.getMonth === 'function' ? Exporter.getReadableDate(value) : '';
    }

    /**
     * @private
     * @param {!Array<!*>}array
     * @param {!number} defaultRows
     * @param {!number} defaultColumns
     * @return {!Array<!*>}
     */
    static arrayWithDefaultIfEmpty(array, defaultRows, defaultColumns) {
        return array?.length === 0 ?
            new Array(defaultRows).fill('').map(() => new Array(defaultColumns).fill('')) :
            array;
    }

    get mappings() {
        return this._mappings;
    }

    get exporters() {
        return this._exporters;
    }
}

/**
 * @enum {number}
 */
Exporter.PRField = {
    prNumber: 0,
    materialId: 1,
    source: 2,
    sourcing: 3,
    contract: 4,
    vendorCode: 5,
    vendorName: 6,
    oneTimeVendor: 7,
    creationDate: 8,
    submissionDate: 9,
    projectTitle: 10,
    wayOfBuying: 11,
    spender: 12,
    requester: 13,
    sbApprover: 14,
    headerDetails: 15,
    internalComment: 16,
    externalComment: 17,
    sendingOptions: 18,
    financialInfo: 19,
    materials: 20,
    attachmentsInfo: 21,
};
